import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CheckCircle2, Mail, Truck, CalendarClock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BASE } from '../lib/api';

export default function OrderConfirmation() {
  const { slug } = useParams();
  const search = new URLSearchParams(useLocation().search);
  const orderId = search.get('order');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    fetch(`${BASE}/api/orders/${orderId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setOrder)
      .catch(() => {});
  }, [orderId]);

  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="telegra-bg" aria-hidden="true" />
      <Header />
      <main className="flex-1 px-4 sm:px-6 pt-[96px] sm:pt-[128px] pb-16 max-w-3xl mx-auto w-full">
        <span className="inline-flex items-center gap-2 text-emerald-700 text-[13px] uppercase tracking-[0.22em] font-semibold bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5">
          <CheckCircle2 size={14} /> Submitted for provider review
        </span>
        <h1 data-testid="order-confirm-title" className="mt-5 font-display uppercase text-[36px] sm:text-[54px] leading-[0.98] tracking-[-0.015em] text-[color:var(--alfa-navy)]">
          You&apos;re on your way.
        </h1>
        <p className="mt-5 text-[16.5px] text-neutral-950 leading-relaxed max-w-2xl">
          Thanks{order?.name ? `, ${order.name.split(' ')[0]}` : ''}. A licensed U.S. provider will review your information and either approve your treatment, request more details, or let you know if it&apos;s not a fit. You will not be charged until your treatment is approved.
        </p>

        {order && (
          <div className="mt-8 rounded-md border border-neutral-900/10 bg-white/85 backdrop-blur p-5 sm:p-6">
            <p className="uppercase text-[12px] tracking-[0.2em] text-neutral-950">Order</p>
            <p className="mt-1 font-display text-[22px] text-neutral-950">{order.product_name}</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[14px]">
              <Info label="Monthly plan" value={`$${order.monthly_price}/mo`} />
              <Info label="Commitment" value={`${order.months} month${order.months>1?'s':''}`} />
              <Info label="Payment method" value={`${order.card_brand || 'Card'} •••• ${order.card_last4 || '••••'}`} />
            </div>
          </div>
        )}

        <div className="mt-8 space-y-3 text-[14px] text-neutral-950">
          <p className="flex items-start gap-2"><Mail size={15} className="mt-0.5 shrink-0" /> A confirmation email is on its way. Watch your inbox for provider updates.</p>
          <p className="flex items-start gap-2"><CalendarClock size={15} className="mt-0.5 shrink-0" /> Provider review typically completes within 24–48 hours.</p>
          <p className="flex items-start gap-2"><Truck size={15} className="mt-0.5 shrink-0" /> Once approved, your first monthly supply ships in discreet packaging.</p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link to="/" className="inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white uppercase text-[14px] tracking-[0.14em] font-semibold px-5 py-3.5 rounded-sm">
            Back to home
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="uppercase text-[11.5px] tracking-[0.18em] text-neutral-950">{label}</p>
      <p className="mt-1 font-display text-[18px] text-neutral-950">{value}</p>
    </div>
  );
}
